import { Activity, RefreshCw } from "lucide-react";
import {
  useAppUsageSummary,
  useRegenerateAppUsageSummary,
} from "../../../helpers/hooks/use-app-usage-summary";

interface Props {
  customerId: string;
}

function relativeTime(iso: string | null): string | null {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Compact card rendered at the top of the App Activity tab. Reads the
 * cron-produced 2-3 sentence narrative; falls back to an empty-state
 * message when the cron hasn't yet generated for this customer.
 */
export function AppUsageSummaryCard({ customerId }: Props) {
  const { data, isLoading, isError } = useAppUsageSummary(customerId);
  const regenerate = useRegenerateAppUsageSummary(customerId);

  const summary = data?.summaryText ?? null;
  const status = data?.status ?? "stale";
  const computedRel = relativeTime(data?.computedAt ?? null);

  return (
    <section className="bg-surface-primary rounded-lg border border-border-secondary shadow-sm p-4 mb-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <Activity className="w-4 h-4 mt-0.5 text-content-tertiary shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-content-primary">
                App usage summary
              </h3>
              {computedRel && (
                <span className="text-[10px] uppercase tracking-wider text-content-tertiary">
                  {computedRel}
                </span>
              )}
              {status === "generating" && (
                <span className="text-[10px] uppercase tracking-wider text-blue-600">
                  generating…
                </span>
              )}
              {status === "error" && (
                <span className="text-[10px] uppercase tracking-wider text-red-600">
                  error
                </span>
              )}
            </div>
            <div className="mt-1.5 text-sm text-content-secondary leading-relaxed whitespace-pre-wrap">
              {isLoading && !summary && (
                <span className="text-content-tertiary">Loading…</span>
              )}
              {isError && !summary && (
                <span className="text-red-600">Failed to load.</span>
              )}
              {!isLoading && !summary && status !== "generating" && (
                <span className="text-content-tertiary">
                  No summary yet — the daily cron generates one at 02:30 ET, or
                  click Regenerate to compute now.
                </span>
              )}
              {summary}
            </div>
            {status === "error" && data?.lastError && (
              <p className="mt-1 text-[11px] text-red-600">{data.lastError}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => regenerate.mutate()}
          disabled={regenerate.isPending || status === "generating"}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-content-secondary hover:text-content-primary disabled:opacity-50"
          title="Force regenerate"
        >
          <RefreshCw
            className={`w-3 h-3 ${regenerate.isPending ? "animate-spin" : ""}`}
          />
          Regenerate
        </button>
      </div>
    </section>
  );
}
